import {
	CircularProgress,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Camera, Settings } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClientKeys } from "web_myplann/Api/Client/ClientKeys";
import { useClientQuery } from "web_myplann/Api/Client/useClientQuery";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { MissingAdvisor } from "web_myplann/Profile/Components/MissingAdvisor";
import { ProfileListItem } from "web_myplann/Profile/Components/ProfileListItem";
import { StyledAvatar } from "web_myplann/Profile/Pages/ProfilePage/styled";
import { MyPlannInput } from "web_myplann/Shared/Components/MyPlannInput";
import { MyPlannLoading } from "web_myplann/Shared/Components/MyPlannLoading";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { getInitials } from "web_myplann/Shared/Utils/getInitials";
import { device } from "web_myplann/Utils/device";

export const ProfilePage = () => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { data: userData, isLoading } = useClientQuery();
	const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

	const clientNotFound = !isLoading && !userData?.data;

	const path = userData?.photo;

	const name = userData?.data
		? `${userData.data.firstName ?? ""} ${userData.data.lastName ?? ""}`.trim()
		: "";
	const phoneNumber = userData?.contactPhone;
	const email = userData?.contactEmail;

	const handlePhotoChange = async () => {
		if (clientNotFound) {
			return;
		}

		setIsUploadingPhoto(true);
		try {
			const response = await device.changeProfileImage();
			if (response.payload.changed) {
				await queryClient.refetchQueries({ queryKey: ClientKeys.all });
			}
		} finally {
			setIsUploadingPhoto(false);
		}
	};

	const renderComponent = () => {
		if (path) {
			return (
				<img
					key={path}
					width={90}
					height={90}
					src={path}
					alt={name}
					style={{
						borderRadius: "50%",
						objectFit: "cover",
					}}
				/>
			);
		}

		return (
			<Typography
				fontSize={40}
				fontWeight={700}
				color={theme.palette.grey[700]}
			>
				{getInitials(name)}
			</Typography>
		);
	};

	return (
		<MyPlannLayout
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.Profile.title)}
					defaultRoute={Routing.Dashboard.path()}
					endComponent={
						<Settings
							cursor="pointer"
							color={theme.palette.primary.main}
							onClick={() =>
								navigate(AppRouting.MyPlann.Profile.Settings.path())
							}
						/>
					}
				/>
			}
		>
			{isLoading ? (
				<MyPlannLoading />
			) : clientNotFound ? (
				<MissingAdvisor />
			) : (
				<Stack p={1.6} gap={1.6}>
					<Stack gap={1.6}>
						<Stack alignItems={"center"} gap={1.6}>
							<StyledAvatar
								sx={{
									overflow: "visible",
									backgroundColor: theme.palette.grey[100],
									position: "relative",
								}}
								onClick={handlePhotoChange}
							>
								{renderComponent()}

								{isUploadingPhoto && (
									<Stack
										sx={{
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											backgroundColor: "rgba(0, 0, 0, 0.5)",
											borderRadius: "50%",
										}}
									>
										<CircularProgress
											size={40}
											sx={{ color: theme.palette.common.white }}
										/>
									</Stack>
								)}

								<IconButton
									disableFocusRipple
									disableRipple
									disableTouchRipple
									sx={{
										position: "absolute",
										bottom: 0,
										right: 0,
										padding: "3px",
										backgroundColor: theme.palette.primary.main,
										"&:hover": {
											backgroundColor: theme.palette.primary.main,
										},
									}}
								>
									<Camera size={16} color="white" strokeWidth={2} />
								</IconButton>
							</StyledAvatar>

							<Typography
								fontSize={24}
								fontWeight={700}
								color={theme.palette.grey[700]}
							>
								{name}
							</Typography>
						</Stack>

						<Stack gap={1.2}>
							<MyPlannInput
								label={t(Resources.Profile.Fields.phone)}
								value={phoneNumber}
								disabled
								slotProps={{
									input: {
										sx: {
											"& .MuiInputBase-input.MuiOutlinedInput-input": {
												WebkitTextFillColor: theme.palette.primary.main,
											},
										},
									},
								}}
							/>

							<MyPlannInput
								label={t(Resources.Profile.Fields.email)}
								value={email}
								disabled
								slotProps={{
									input: {
										sx: {
											"& .MuiInputBase-input.MuiOutlinedInput-input": {
												WebkitTextFillColor: theme.palette.primary.main,
											},
										},
									},
								}}
							/>
						</Stack>
					</Stack>

					<Stack gap={0.8}>
						<ProfileListItem
							label={t(Resources.Profile.Sections.PersonalDataAndAccounts)}
							onClick={() =>
								navigate(
									AppRouting.MyPlann.Profile.PersonalDataAndAccounts.path(),
								)
							}
							disabled={clientNotFound}
						/>
						<ProfileListItem
							label={t(Resources.Profile.Sections.Addresses)}
							onClick={() =>
								navigate(AppRouting.MyPlann.Profile.Addresses.path())
							}
							disabled={clientNotFound}
						/>
						<ProfileListItem
							label={t(Resources.Profile.Sections.Paperwork)}
							onClick={() =>
								navigate(AppRouting.MyPlann.Profile.Paperwork.path())
							}
							disabled={clientNotFound}
						/>
						<ProfileListItem
							label={t(Resources.Profile.Sections.FinancialBalance)}
							onClick={() =>
								navigate(AppRouting.MyPlann.Finances.Intro.path(), {
									state: { from: "profile" },
								})
							}
							noDivider
							disabled={clientNotFound}
						/>
					</Stack>
				</Stack>
			)}
		</MyPlannLayout>
	);
};
